import { tripPlannerScopeKey } from '@luxuryescapes/contract-trip'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import {
  ASSISTANT_SURFACE_LABEL_MAX_LENGTH,
  sanitizeAssistantSurfacePlainText,
  toAssistantSurfaceId,
} from 'tripPlanner/assistant/surfaceSnapshots/assistantSurfacePlainText'
import type {
  AssistantSurfaceSnapshotChunk,
  AssistantSurfaceSnapshotChunkManifest,
  SurfaceCapabilityRequest,
  SurfaceManifestFactRequest,
  SurfaceVisibleStateRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import type { AssistantSurfaceSnapshotBuildResult } from 'tripPlanner/assistant/surfaceSnapshots/useAssistantSurfaceSnapshot'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { sortCommittedState } from 'tripPlanner/assistant/surfaceSnapshots/committedState'
import type { DrawerStackEntry } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerStackState'
import type { CartItemPrice, TurnAuthoritySnapshot } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/TurnAuthoritySnapshotContext'
import { assistantSurfaceSnapshotChunkContentHash, buildAssistantSurfaceSnapshotChunkRef } from './fingerprint'

const capabilities: Array<SurfaceCapabilityRequest> = [{
  capability: 'surface_context.read',
  mode: 'available',
}]

// Trims trailing gaps (`undefined` OR `''` slots with nothing after them) so a caller with
// independently-optional parts (e.g. offerDrawer's offerType/propertyId) can pass a fixed-arity
// array -- an absent slot is `undefined`, not omitted from the array entirely, so a present later
// slot never shifts into an earlier one's position. Used both up front and again after every
// tail-drop inside composeIdentityDescriptor below (see its own comment for why both call sites
// matter). Treating `''` the same as `undefined` here (not just at the call site) is defense in
// depth: today's one caller already converts an absent optional part to `undefined` before this
// ever sees it, but the whole point of this refactor was to stop a dangling trailing delimiter
// from reappearing -- a future caller that forgets that conversion shouldn't reintroduce it.
function trimTrailingGaps(parts: Array<string | undefined>): Array<string | undefined> {
  const trimmed = [...parts]
  while (trimmed.length > 0 && !trimmed[trimmed.length - 1]) trimmed.pop()
  return trimmed
}

// svc-trip validates activeDrawer as plainTextString(ASSISTANT_SURFACE_LABEL_MAX_LENGTH) --
// exceeding it, or including markdown/HTML/a custom-protocol-looking substring, 400s the whole
// ensure call, degrading the entire turn (read tool + every cart-price fact) precisely when a
// drawer is open. Composes required identity parts first, dropping optional parts from the tail
// (in order) if the result would exceed the cap, and omitting the whole descriptor (never
// truncating a part directly) only if even the required parts don't fit -- an id truncated
// mid-string would silently misdirect a lookup_offer/lookup_google_places call with nothing to
// catch it, which is worse than the model simply not seeing an active drawer this turn.
// '|' (not ':') is this file's delimiter for composing identity parts together, specifically so
// an id part is never ambiguous with the delimiter: toAssistantSurfaceId's safe charset
// deliberately allows ':' (some callers rely on it for their own already-meaningful compound
// ids, e.g. experience offerIds are `providerKey:externalId` -- see offerUtils.ts's
// detectOfferTypeFromId), so joining with ':' would make `offerDrawer:provider123:extRef456`
// read as *three* colon-delimited segments instead of two -- a model extracting "the offerId" to
// pass to lookup_offer could easily grab just `provider123` and silently drop `extRef456`. That
// failure is worse than the collision itself: it isn't a crash or a 400, it's a wrong tool-call
// argument with nothing to catch it. Collapsing the id's own colons instead (e.g. to '-') would
// avoid the ambiguity but corrupts the id itself, which is exactly what this file already
// forbids for truncation and shouldn't allow here either. Picking a delimiter outside the id
// charset sidesteps both: no id can ever contain '|', so no id part needs to change at all. Free
// text (locationInfo/placeSearchPending) isn't charset-restricted, so it could in principle still
// contain a literal '|', but that's a structural-ambiguity risk over free text with no id to
// protect, not an id-corruption risk -- and far less likely in practice than ':' was.
//
// optionalParts is fixed-arity (Array<string | undefined>), not "present parts only" -- an
// independently-optional part (e.g. offerDrawer's offerType/propertyId) must occupy its own slot
// even when absent, or a present later part silently shifts into an earlier absent one's position.
// Trailing gaps are trimmed (via trimTrailingGaps) both up front and again after every tail-drop
// below, since dropping the last defined slot for length can expose a new trailing gap in what's
// left -- doing this in one place keeps the two policies from drifting apart the way they did when
// a caller trimmed once and this function dropped afterwards.
function composeIdentityDescriptor(requiredParts: Array<string>, optionalParts: Array<string | undefined>): string | undefined {
  const parts = trimTrailingGaps(optionalParts)
  for (let dropCount = 0; dropCount <= parts.length; dropCount++) {
    const kept = trimTrailingGaps(parts.slice(0, parts.length - dropCount))
    const candidate = [...requiredParts, ...kept.map(part => part ?? '')].join('|')
    if (candidate.length <= ASSISTANT_SURFACE_LABEL_MAX_LENGTH) {
      return candidate
    }
  }
  return undefined
}

// Per-variant identity, matching useDrawerStackState's own isSameDrawerTarget comparison
// exactly -- the entry's top-level `id` is a push-order counter, not a content identity, so
// it is never used here. Doubles as a cheap pointer into lookup_offer/lookup_google_places
// (both unconditionally available to trip_planner turns) for the two variants with a real
// place/offer id; locationInfo and placeSearchPending have no matching lookup tool, but the
// descriptive string still gives the model useful context.
//
// Two different sanitization strategies, not one blanket sanitizer over the whole composed
// string: detailPanel/placeInfo/offerDrawer embed a real id the model may read back out for a
// lookup call, so their ids are normalized to a safe charset via toAssistantSurfaceId (never
// truncated) and only the non-id parts are droppable under length pressure. locationInfo/
// placeSearchPending carry only free text (LLM-emitted titles/search queries, no length or
// charset guarantee) with no id to protect -- but they still go through composeIdentityDescriptor
// rather than being individually bounded then re-bounded as a whole: bounding `title` to the full
// 80-char budget on its own, then re-bounding the composed `locationInfo|${title}|${placeSearch}`
// string a second time, silently drops placeSearch entirely (not just shortens it) for any title
// past ~67 chars, since the kind prefix alone already leaves no room once title has already
// claimed the whole budget. Sanitizing (not bounding) each field and letting
// composeIdentityDescriptor own the one length/drop decision avoids that.
function describeDrawerEntry(entry: DrawerStackEntry): string | undefined {
  switch (entry.kind) {
    case 'detailPanel': {
      const placeId = toAssistantSurfaceId(entry.data.googlePlaceId)
      if (placeId === undefined) return undefined
      const stopId = entry.data.stopId !== undefined ? [String(entry.data.stopId)] : []
      return composeIdentityDescriptor(['detailPanel', placeId], stopId)
    }
    case 'placeInfo': {
      const placeId = toAssistantSurfaceId(entry.data.googlePlaceId)
      return placeId !== undefined ? composeIdentityDescriptor(['placeInfo', placeId], []) : undefined
    }
    case 'placeSearchPending': {
      const searchQuery = sanitizeAssistantSurfacePlainText(entry.data.searchQuery)
      return searchQuery !== undefined ? composeIdentityDescriptor(['placeSearchPending', searchQuery], []) : undefined
    }
    case 'locationInfo': {
      const title = sanitizeAssistantSurfacePlainText(entry.data.title)
      if (title === undefined) return undefined
      const placeSearch = sanitizeAssistantSurfacePlainText(entry.data.placeSearch)
      return composeIdentityDescriptor(['locationInfo', title], placeSearch !== undefined ? [placeSearch] : [])
    }
    case 'offerDrawer': {
      const offerId = toAssistantSurfaceId(entry.data.offerId)
      if (offerId === undefined) return undefined
      // offerType/propertyId passed straight through as their own fixed-arity slots (undefined
      // when absent) -- composeIdentityDescriptor owns filling, trimming, and tail-dropping them
      // together, so a present propertyId can never shift into offerType's position just because
      // offerType alone is missing (offerDrawer|{offerId}||{propertyId} instead).
      const propertyId = toAssistantSurfaceId(entry.data.accommodationPropertyId)
      return composeIdentityDescriptor(['offerDrawer', offerId], [entry.data.offerType || undefined, propertyId])
    }
    case 'listingSearch': {
      const placeName = sanitizeAssistantSurfacePlainText(entry.data.placeName)
      if (placeName === undefined) return undefined
      // Vertical rides in its own slot rather than the identity: two drawers for
      // the same place differ only by which listing they show.
      return composeIdentityDescriptor(['listingSearch', placeName], [entry.data.vertical])
    }
    default:
      return assertUnreachable(entry)
  }
}

// Price display and checkout eligibility are independent. Unpriced items still carry
// readiness/blockers, and complete item groups are retained under the manifest budget.
function buildCartItemManifestFacts(
  { itemId, checkoutPrice, countsTowardTotal, isBookingReady, checkoutBlocker }: CartItemPrice,
  displayedPrice: string | undefined,
): Array<SurfaceManifestFactRequest> {
  const facts: Array<SurfaceManifestFactRequest> = []
  if (displayedPrice !== undefined) {
    facts.push({ key: `cart_displayed_price:${itemId}`, value: displayedPrice, dataClassification: 'commercial_surface' })
  }
  if (checkoutPrice !== undefined) {
    facts.push({ key: `cart_checkout_price:${itemId}`, value: checkoutPrice, dataClassification: 'commercial_surface' })
  }
  facts.push(
    { key: `cart_counts_toward_total:${itemId}`, value: countsTowardTotal ? 'true' : 'false', dataClassification: 'commercial_surface' },
    { key: `cart_is_booking_ready:${itemId}`, value: isBookingReady ? 'true' : 'false', dataClassification: 'commercial_surface' },
  )
  if (checkoutBlocker !== undefined) {
    facts.push({ key: `cart_checkout_blocker:${itemId}`, value: checkoutBlocker, dataClassification: 'commercial_surface' })
  }
  return facts
}

function buildVisibleState(turnAuthoritySnapshot: TurnAuthoritySnapshot): SurfaceVisibleStateRequest {
  const manifestFacts: Array<SurfaceManifestFactRequest> = []
  const displayedPrices = new Map(turnAuthoritySnapshot.displayedCartItemPrices?.map(item => [item.itemId, item.displayedPrice]))
  const summary = turnAuthoritySnapshot.cartSummary
  if (summary) {
    manifestFacts.push({ key: 'cart_status', value: summary.status, dataClassification: 'commercial_surface' })
    manifestFacts.push({ key: 'cart_ready_item_count', value: String(summary.readyItemCount), dataClassification: 'commercial_surface' })
    if (summary.estimatedTotal !== undefined) manifestFacts.push({ key: 'cart_estimated_total', value: summary.estimatedTotal, dataClassification: 'commercial_surface' })
  }
  for (const cartItemPrice of turnAuthoritySnapshot.cartItemPrices) {
    manifestFacts.push(...buildCartItemManifestFacts(cartItemPrice, displayedPrices.get(cartItemPrice.itemId)))
  }

  if (summary || turnAuthoritySnapshot.cartItemPrices.length > 0) {
    manifestFacts.unshift(
      { key: 'cart_total_item_count', value: String(turnAuthoritySnapshot.cartItemPrices.length), dataClassification: 'commercial_surface' },
    )
  }

  const centralDrawer = turnAuthoritySnapshot.isAnyDrawerOpen && turnAuthoritySnapshot.topEntry
    ? describeDrawerEntry(turnAuthoritySnapshot.topEntry)
    : undefined

  // Central drawers render above the domain drawer base. Descriptions enrich only
  // their own entry; hydration cannot promote an underlying drawer over that entry.
  const centralEntryId = centralDrawer ? turnAuthoritySnapshot.topEntry?.id : undefined
  const domainDrawer = Array.from(turnAuthoritySnapshot.visibleDrawers?.values() ?? [])
    .filter(drawer => drawer.centralEntryId === centralEntryId)
    .at(-1)
  const activeDrawer = sanitizeAssistantSurfacePlainText(
    turnAuthoritySnapshot.mobileOverlay ?? domainDrawer?.label ?? centralDrawer ?? '',
  )?.slice(0, ASSISTANT_SURFACE_LABEL_MAX_LENGTH) || undefined

  return {
    ...(turnAuthoritySnapshot.rightPanelView ? { primaryMode: turnAuthoritySnapshot.rightPanelView } : {}),
    ...(activeDrawer !== undefined ? { activeDrawer } : {}),
    ...(manifestFacts.length > 0 ? { manifestFacts } : {}),
  }
}

// No count cap here: useSurfaceActivityAccumulator's capAndTrackDrops already enforces the
// 40-entry cap (matching the backend's own ASSISTANT_SURFACE_ACTIVITY_MAX_COUNT) on every path
// that can grow its buffer -- push and restore alike -- so drainSurfaceActivity()'s return value,
// this function's only real input, can never exceed it. Re-capping here would duplicate that
// invariant in a second place instead of trusting the one place it's actually enforced.
//
// reverse() gives newest-first on the wire. Presentational, not load-bearing -- the backend
// selects on occurredAt regardless of wire order -- but kept because it makes a captured request
// read the way the model receives it. No sort function needed here either: push order IS
// occurredAt order by the time entries reach this point, because restoreDrainedActivity is the
// one place that invariant could break under overlapping restores, and it re-sorts there rather
// than leaving every downstream consumer (including this one) to distrust push order defensively.
function buildRecentSurfaceActivity(entries: Array<TripPlannerSurfaceActivityEntry>): Array<TripPlannerSurfaceActivityEntry> {
  return [...entries].reverse()
}

function buildCommittedStateChunk(recentCommittedState: Array<TripPlannerCommittedStateEntry>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'committed',
    kind: 'recent_committed_state',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'recent_committed_state', recentCommittedState }),
    recentCommittedState,
  }
}

function buildCapabilityChunk(): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'trip_planner',
    kind: 'capabilities',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'capabilities', capabilities }),
    capabilities,
  }
}

export interface TripPlannerSnapshotInput {
  chatId: number
  turnAuthoritySnapshot: TurnAuthoritySnapshot
  surfaceActivityEntries: Array<TripPlannerSurfaceActivityEntry>
  committedStateEntries: Array<TripPlannerCommittedStateEntry>
}

export function buildTripPlannerSnapshot(input: TripPlannerSnapshotInput): AssistantSurfaceSnapshotBuildResult {
  const surfaceContextScopeKey = tripPlannerScopeKey({ chatId: input.chatId })
  const visibleState = buildVisibleState(input.turnAuthoritySnapshot)
  const selectedFlights = input.turnAuthoritySnapshot.selectedFlightEntries
  const recentCommittedState = sortCommittedState(input.committedStateEntries.map(entry => {
    if (entry.kind !== 'selected_flight_itinerary' || !selectedFlights) return entry
    const selected = selectedFlights.get(entry.key)
    return { ...(selected ?? { ...entry, displayText: 'No flight currently selected for this leg' }), expiresAt: entry.expiresAt }
  }))
  const chunks = [buildCommittedStateChunk(recentCommittedState), buildCapabilityChunk()]
  const recentSurfaceActivity = buildRecentSurfaceActivity(input.surfaceActivityEntries)

  const manifest: AssistantSurfaceSnapshotChunkManifest = {
    surface: 'trip_planner',
    schemaVersion: 1,
    surfaceContextScopeKey,
    visibleState,
    ...(recentSurfaceActivity.length > 0 ? { recentSurfaceActivity } : {}),
    chunks: chunks.map(buildAssistantSurfaceSnapshotChunkRef),
  }

  return {
    chunks,
    manifest,
    snapshot: {
      surface: 'trip_planner',
      schemaVersion: 1,
      surfaceContextScopeKey,
      visibleState,
      ...(recentSurfaceActivity.length > 0 ? { recentSurfaceActivity } : {}),
      atoms: [],
      targets: [],
      recentCommittedState,
      capabilities,
    },
  }
}
